import clsx from 'clsx';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LocaleContext } from 'components/rebrand/Layout';

import { IQueryParams, ITrackEventProps } from 'typescriptDefs/index';

import { getAppOnboardingLink, getWebOnboardingLink } from 'helpers/getOnboardingLinks';
import { trackEvent, trackEventUniversal, useGetQueryParams, useIsMobile } from 'helpers/index';
import { useRegistrationParams } from 'utils/hooks/use-registration-params-context';

import { Button, ButtonProps, ButtonVariant } from './Button';
import { OpenAppModal } from './OpenAppModal';

export interface GetKOHOBtnProps {
  buttonClassName?: string;
  buttonLocation: string;
  buttonSize?: ButtonProps['size'];
  buttonVariant?: ButtonVariant;
  children?: ReactNode;
  className?: string;
  onClick?: () => void;
  queryParams: IQueryParams;
  to?: string;
  trackEventProps: ITrackEventProps;
}

export const GetKOHOBtn = ({
  buttonClassName,
  buttonLocation,
  buttonSize,
  buttonVariant,
  children,
  className,
  onClick,
  queryParams,
  to,
  trackEventProps,
  ...props
}: GetKOHOBtnProps) => {
  const isMobile = useIsMobile();
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
  const [downloadAppModalOpen, setOpenDownloadAppModal] = useState(false);
  const { sendUserToApp, name, appOneLink, intent } = useRegistrationParams();
  const locale = useContext(LocaleContext);

  const baseParams = useGetQueryParams(locale, queryParams);

  const webParams = useGetQueryParams('en', {
    search: { ...queryParams.search, af_sub1: referralCode, ...(intent ? { intent } : undefined) },
    hash: { ...queryParams.hash, referral: referralCode },
  });

  const mobileTrackingSearchParams = {
    af_dp: 'koho%3A%2F%2F',
    // pid must be set to "partner" for mobile app to populate referral code on registration
    pid: referralCode ? 'partner' : 'sms-reg',
    // campaign must be set to referralCode to populate referral code on registration
    c: referralCode ?? 'sms-reg',
  };

  const openMobileAppParams = useGetQueryParams(locale, {
    search: {
      ...queryParams.search,
      ...mobileTrackingSearchParams,
      af_sub4: `sms-${name}-${isMobile ? 'button' : 'qrcode'}`,
    },
    hash: {},
  });
  const smsParams = useGetQueryParams(
    undefined,
    { search: { ...mobileTrackingSearchParams, af_sub4: `sms-${name}-sms` }, hash: {} },
    { ignoreCookies: true },
  );

  useEffect(() => {
    const localStorageReferralCode = localStorage.getItem('kohoReferralCode');
    const urlParamReferralCode = baseParams.hash.referral ?? baseParams.search.af_sub1;

    const referralCodeParam = urlParamReferralCode ?? localStorageReferralCode;
    if (referralCodeParam) setReferralCode(referralCodeParam);
  }, [setReferralCode, baseParams]);

  const onboardingLink = sendUserToApp
    ? getAppOnboardingLink(openMobileAppParams, appOneLink)
    : getWebOnboardingLink(webParams);
  const smsOnboardingLink = getAppOnboardingLink(smsParams, appOneLink);

  const defaultText = { en: 'Open Account', fr: 'Ouvrir un compte' }[locale];

  return (
    <>
      {isMobile || !sendUserToApp ? (
        <a
          aria-label={
            {
              en: `Redirect to onboarding`,
              fr: `Redirection vers le site d'accueil`,
            }[locale]
          }
          rel="noopener noreferrer"
          target="_self"
          href={to ?? onboardingLink}
          onClick={() => {
            trackEventUniversal('Navigate to Onboarding', {
              event_label: buttonLocation,
            });
            trackEventUniversal('"Get KOHO" clicked', {
              event_label: buttonLocation,
            });
            if (isMobile && sendUserToApp) {
              trackEvent('Clicked onboarding button', { ...trackEventProps, name: 'open-app-on-mobile' });
            } else {
              trackEvent('Clicked onboarding button', trackEventProps);
            }

            onClick && onClick();
          }}
          className={twMerge(clsx('w-fit', className))}
          {...props}
        >
          <Button role="none" tabIndex={-1} className={buttonClassName} variant={buttonVariant} size={buttonSize}>
            {children ?? defaultText}
          </Button>
        </a>
      ) : (
        <Button
          id="open-app-button"
          className={twMerge(className, buttonClassName)}
          variant={buttonVariant}
          size={buttonSize}
          onClick={() => {
            trackEvent('Clicked', { ...trackEventProps, name: 'open-download-app-modal' });

            setOpenDownloadAppModal(true);
          }}
        >
          {children ?? defaultText}
        </Button>
      )}
      {downloadAppModalOpen && (
        <OpenAppModal
          isOpen={downloadAppModalOpen}
          closeModal={() => setOpenDownloadAppModal(false)}
          qrCodeLink={onboardingLink}
          smsLink={smsOnboardingLink}
          trackEventProps={trackEventProps}
          intent={intent}
        />
      )}
    </>
  );
};
